/**
 * The navigation manifest for the Fleet Console.
 *
 * CONSOLES.md 5: "Navigation as a manifest ... Every route exists day one. A route with
 * `status: dayN` renders an `EmptyState` naming what it is waiting on."
 *
 * So this file is COMPLETE on the first commit. Later work changes a `status` from `dayN`
 * to `live` and adds a screen module under `src/screens/`. It NEVER adds a route here.
 * If you are about to add one, the manifest was wrong on day one and that is the finding.
 *
 * The shape follows the Admin Console's `src/domain/navigation.ts` deliberately: groups of
 * items, a `perm` per item declared now and enforced server side, an `icon`, and the unbuilt
 * copy DERIVED from the route rather than hand-written per route. CLAUDE.md says reuse those
 * patterns rather than reinvent them, and thirty bespoke empty states would drift the moment
 * one of them landed.
 *
 * This module is free of React and of any framework import: it is pure data plus pure
 * functions, so `src/routes/routes.check.ts` can assert over it with nothing installed.
 */

/**
 * `live` means a real screen renders. `dayN` means an EmptyState renders, naming what it
 * is waiting on. The vocabulary is CONSOLES.md 5's, verbatim, so that "change a status
 * from dayN to live" is a literal instruction rather than a paraphrase.
 */
export type RouteStatus = 'live' | 'dayN'

/**
 * The rails from `components/c-fleet-console.md`, plus DECKS: the Deck Server's admin surface,
 * moved here from the standalone deck server. Adding it is the one recorded exception to "the
 * manifest is complete on day one" (approved by Mark via Eric, 28 October; the exception itself is
 * recorded in the fleet-api claude-md source, and CONSOLES.md section 2.12 names it).
 */
export type NavSectionId = 'fleet' | 'clients' | 'library' | 'platform' | 'decks'

/**
 * Where a screen's data comes from. CONSOLES.md 6: the Fleet Console NEVER reads customer
 * data. Everything it shows comes from the fifteen upward message types, which are counts,
 * states and durations by construction. Declaring the source per route turns that paragraph
 * into something `routes.check.ts` can fail on, which is the only form of it that survives
 * a busy week.
 */
export type RouteSource =
  | 'our-record' // clients, entitlement, releases, the ledger: rows we own outright
  | 'upward' // one of the fifteen upward message types
  | 'derived' // computed from the two above (last seen, snapshot lag, trends)
  | 'deep-link' // a pointer into THEIR tenant. We render the link, never a copy (2.2)
  | 'none' // shell furniture: not-found, redirects

export interface RouteNode {
  /** Stable id. Screens bind to this, not to the path, so a path can be corrected. */
  id: string
  /** Absolute for a top-level route, relative for a child. An empty string is the index child. */
  path: string
  title: string
  /** Lucide icon name, kebab-case, same convention as the Admin Console rail. */
  icon: string
  /**
   * The capability this route needs, declared now and ENFORCED SERVER SIDE, read from the
   * same entitlement record the API enforces (CONSOLES.md 0). Nothing in this app reads it
   * as a gate. Declaring it now means the day it is enforced is a filter over this manifest
   * rather than a hunt through components, and an unentitled route answers 404 rather than
   * 403, because a 403 confirms the thing exists.
   */
  perm: string
  /**
   * Capabilities the CONTROLS on this screen need, as opposed to `perm`, which is what
   * reaching the screen at all needs.
   *
   * ONE `perm` COULD NOT SAY THIS, and that was a real gap rather than a tidiness point.
   * Every route here declared a `.read` or `.list` capability except one, while five write
   * operations shipped on four LIVE screens: `clients.list` creates a client under
   * `client.list`, `clients.detail.overview` patches the record AND ISSUES AN ENROLLMENT
   * BUNDLE under `client.read`, and `library.definitionKind` publishes a definition and
   * records the D35 eval pass under `definition.read`. Only `fleet.deployment.actions`
   * carried a write capability at all.
   *
   * Collapsing the two into one field is worse in both directions: setting the route's
   * `perm` to the write capability locks a read-only operator out of a screen they are
   * entitled to see, and leaving it as the read capability says that an operator who can
   * read a client can mint a credential for one. So they are declared separately.
   *
   * `bundle.issue` is deliberately NOT folded into `client.write`. Minting a single-use
   * enrollment credential is the highest-privilege act on this surface -- it is what lets a
   * deployment enroll at all -- and it is not the same permission as fixing a typo in a name.
   *
   * Nothing in this app enforces these yet, exactly as `perm` is not enforced yet. The point
   * is identical: the day it is enforced is a filter over this manifest rather than a hunt
   * through components, and the SERVER refuses independently regardless.
   */
  writes?: string[]
  /** The plan section this route implements. Asserted against CONSOLES.md by the check. */
  spec: string
  status: RouteStatus
  source: RouteSource
  /**
   * What an EmptyState says while `status` is `dayN`. Required whenever it is: a rail item
   * that does not say what it is waiting on is the item people assume is broken
   * (wf-11 U-05: an EmptyState that says which day it lands, because a rail item that lies
   * is worse than one that is missing).
   */
  waitingOn?: string
  /**
   * The lane in `docs/plan/lanes.json` that fills this screen, or `null` when no lane's
   * stated scope covers it. `null` is not a placeholder to tidy up later. It is the hole,
   * made visible on the screen itself.
   */
  lane: string | null
  /**
   * True where the screen shows more than one client at once. CONSOLES.md 0 makes these
   * role-gated, because they are the only place another client's operational shape is
   * visible at all. Like `perm`, this ENFORCES NOTHING: it records which routes carry the
   * obligation. A role filtered in the browser is a suggestion.
   */
  crossClient?: boolean
  /** Shown on the rail. Detail routes and index children are reached from a parent. */
  inRail?: boolean
  children?: RouteNode[]
}

export interface NavSection {
  id: NavSectionId
  title: string
  routes: RouteNode[]
}

const L2 = 'L2-console-fleet'

/**
 * The lane that filled the CLIENTS rail.
 *
 * `clients.list` carried `lane: null` and a `waitingOn` that said "No lane in lanes.json names
 * this screen in its scope", and both were true when they were written and then stopped being
 * true without anything going red. `L2-console-clients` is that scope, and its `owns` was
 * widened twice to reach it. A stale hole is worse than an open one: it is what sends the next
 * person looking for a lane that is already sitting in the map.
 */
const L2_CLIENTS = 'L2-console-clients'
const CONSOLE_ACCESS = 'console-access-2026-08-27'

/** The lane that filled the DECKS rail. */
const L2_DECKS = 'L2-console-decks'

/**
 * The MAP workspace lane. NOT in `lanes.json` yet, and that is the second half of the finding
 * recorded below: the server half lives in `services/console-api/**`, which `L1-api-clients`
 * owns in the fleet-api repo, so this surface spans two lanes in two repositories and neither
 * of them names it today.
 */
const L2_MAP = 'L2-console-map'

/**
 * The manifest. Every route in CONSOLES.md 2 and every screen in c-fleet-console.md is
 * here, plus the two connector stores CONSOLES.md 4.5 assigns to this console (D31).
 */
export const MANIFEST: NavSection[] = [
  {
    id: 'fleet',
    title: 'FLEET',
    routes: [
      {
        id: 'fleet.overview',
        path: '/',
        title: 'Fleet Overview',
        icon: 'layout-dashboard',
        perm: 'deployment.list',
        spec: 'CONSOLES.md §2.1',
        status: 'live',
        source: 'upward',
        // 2.1: default sort is last seen, oldest first. The deployments you have not heard
        // from are the ones that need you, and silence is the primary failure signal in a
        // deployment-initiated architecture.
        waitingOn: 'Deployment telemetry landing (wf-04 E-01)',
        lane: L2,
        inRail: true,
      },
      {
        id: 'fleet.deployments',
        path: '/fleet/deployments',
        title: 'Deployments',
        icon: 'server',
        perm: 'deployment.list',
        spec: 'c-fleet-console.md · FLEET > Deployments',
        status: 'live',
        source: 'upward',
        waitingOn: 'Enrollment state and the upgrade queue (wf-01, wf-03)',
        lane: L2,
        inRail: true,
      },
      {
        id: 'fleet.deployment',
        path: '/fleet/deployments/:deploymentId',
        title: 'Deployment',
        icon: 'server',
        perm: 'deployment.read',
        spec: 'CONSOLES.md §2.2',
        status: 'live',
        source: 'upward',
        waitingOn: 'One deployment, upstream and downstream in one place',
        lane: L2,
        children: [
          {
            id: 'fleet.deployment.identity',
            path: '',
            title: 'Identity',
            icon: 'fingerprint',
            perm: 'deployment.read',
            spec: 'CONSOLES.md §2.2',
            status: 'live',
            source: 'our-record',
            // Credential expiry carries a warning inside 30 days (2.2).
            waitingOn: 'Client, tenant, subscription, region, enrollment date and credential expiry',
            lane: L2,
          },
          {
            id: 'fleet.deployment.components',
            path: 'components',
            title: 'Components',
            icon: 'boxes',
            perm: 'deployment.read',
            spec: 'CONSOLES.md §2.2',
            status: 'live',
            source: 'upward',
            waitingOn: 'DEPLOYMENT.STATE component versions, health and last restart',
            lane: L2,
          },
          {
            id: 'fleet.deployment.snapshot',
            path: 'snapshot',
            title: 'Snapshot',
            icon: 'layers',
            perm: 'deployment.read',
            spec: 'CONSOLES.md §2.2',
            status: 'live',
            source: 'upward',
            waitingOn: 'Current snapshot, definition set version, what is pending, what failed to apply and why',
            lane: L2,
          },
          {
            id: 'fleet.deployment.estate',
            path: 'estate',
            title: 'Estate',
            icon: 'cloud',
            perm: 'deployment.read',
            spec: 'CONSOLES.md §2.2',
            status: 'live',
            source: 'upward',
            waitingOn:
              'Capacity SKU, region and policy denials by name. Things about their Azure we detect rather than control',
            lane: L2,
          },
          {
            id: 'fleet.deployment.pushHistory',
            path: 'push-history',
            // History (nth-ai/fleet-console#209): pushes are one of five sources now, and the
            // path stays so a link to it still lands.
            title: 'History',
            icon: 'send',
            perm: 'deployment.read',
            spec: 'CONSOLES.md §2.2',
            status: 'live',
            source: 'our-record',
            waitingOn:
              'Everything that happened to this deployment: console actions, updates, enrolment, ' +
              'credential rotations and pushes with their outcome (#209)',
            lane: L2,
          },
          {
            id: 'fleet.deployment.telemetry',
            path: 'telemetry',
            title: 'Telemetry',
            icon: 'activity',
            perm: 'telemetry.read',
            spec: 'CONSOLES.md §2.2',
            status: 'live',
            source: 'upward',
            waitingOn: 'The five phase-1 message types, live (wf-04)',
            lane: L2,
          },
          {
            id: 'fleet.deployment.egress',
            path: 'egress',
            // Exchange (#209): our record of every call this deployment's Relay made, with the
            // body we stored (NthAI.md rule 4), and a link to their console. Their egress log is
            // never copied here: it stays in their tenant and the page says so.
            title: 'Exchange',
            icon: 'file-text',
            perm: 'egress.read',
            spec: 'CONSOLES.md §2.2',
            status: 'live',
            source: 'our-record',
            waitingOn:
              "The Fleet API's record of this Relay's calls, both directions, and a link to their console (#209)",
            lane: L2,
          },
          {
            id: 'fleet.deployment.actions',
            path: 'actions',
            title: 'Actions',
            icon: 'zap',
            perm: 'deployment.write',
            writes: ['deployment.write', 'bundle.issue'],
            spec: 'CONSOLES.md §2.2',
            status: 'live',
            source: 'our-record',
            waitingOn: 'Push a definition set, pin a version, force re-enroll, decommission (D30)',
            lane: L2,
          },
        ],
      },
      {
        id: 'fleet.pushHistory',
        path: '/fleet/push-history',
        // History (#209): the path stays so a link to it still lands.
        title: 'History',
        icon: 'send',
        perm: 'deployment.read',
        spec: 'CONSOLES.md §2.5',
        status: 'live',
        source: 'our-record',
        waitingOn:
          'Everything that happened to every deployment, pushes and rollbacks among it as entries of their own (#209)',
        lane: L2,
        crossClient: true,
        inRail: true,
      },
      {
        id: 'fleet.health',
        path: '/fleet/health',
        title: 'Fleet health',
        icon: 'heart-pulse',
        perm: 'fleet.read',
        spec: 'CONSOLES.md §2.8',
        status: 'live',
        source: 'upward',
        // Built 30 August from telemetry that was already arriving: `templateKey` and
        // `coverage` on CONNECTOR.HEALTH, `agentKey`, `status`, `errorClass` and `tokenCost`
        // on RUN.TELEMETRY, `estate.capacitySku` on DEPLOYMENT.STATE. Nothing new was needed
        // upward - the aggregate views simply had nobody to render them.
        waitingOn:
          'Connector health by template, coverage distribution, agent failure rates and token cost by agent key',
        // `lanes.json` names PATHS, not screens, and L2-console-fleet owns
        // `src/screens/fleet/**` - which is where this screen was built. So the note that used
        // to sit here, "no lane in lanes.json names this screen in its scope", was true of the
        // screen list and never of the file. The route check is the thing that surfaced it:
        // flipping this to `live` with `lane: null` fails "a live route is one somebody owns",
        // which is accountability working rather than a field needing filling in.
        lane: L2,
        crossClient: true,
        inRail: true,
      },
    ],
  },
  {
    id: 'clients',
    title: 'CLIENTS',
    routes: [
      {
        id: 'clients.list',
        path: '/clients',
        title: 'Clients',
        icon: 'users',
        perm: 'client.list',
        writes: ['client.write'],
        spec: 'CONSOLES.md §2.3',
        status: 'live',
        source: 'our-record',
        // A client is created BEFORE any Azure resource exists (c-fleet-console.md), and
        // the Fleet Console client record is the identity (D42).
        waitingOn: 'The origin list, and the create that starts a client. Filled by L2-console-clients (wf-12 E-01)',
        lane: L2_CLIENTS,
        crossClient: true,
        inRail: true,
      },
      {
        id: 'clients.detail',
        path: '/clients/:clientId',
        title: 'Client',
        icon: 'building-2',
        perm: 'client.read',
        spec: 'CONSOLES.md §2.3',
        status: 'live',
        source: 'our-record',
        waitingOn: 'One client record, and the five sections below it',
        lane: L2_CLIENTS,
        children: [
          {
            id: 'clients.detail.overview',
            path: '',
            title: 'Overview',
            icon: 'layout-dashboard',
            perm: 'client.read',
            writes: ['client.write', 'bundle.issue'],
            spec: 'CONSOLES.md §2.3',
            status: 'live',
            source: 'our-record',
            // The verification ladder IS this screen, and the two axes are two axes. E-02 to
            // E-05 all land here.
            waitingOn: 'The verification ladder, the two independent axes, and the bundle shown once',
            lane: L2_CLIENTS,
          },
          {
            id: 'clients.detail.deployments',
            path: 'deployments',
            title: 'Deployments',
            icon: 'server',
            perm: 'deployment.list',
            // Add deployment issues a bundle, and each bundle and deployment row opens its own
            // install package. The package used to be a tab of its own (`install-package`), where
            // the naming was typed rather than read from the enrollment it belongs to; that route
            // is REMOVED, not parked at `dayN`, because a package for a client in general is the
            // thing that no longer exists. It is the one route this manifest has lost, and it is
            // recorded here so the next reader does not restore it as a gap.
            writes: ['bundle.issue'],
            spec: 'CONSOLES.md §2.3',
            status: 'live',
            source: 'our-record',
            // D39 and D41: a client may have several, and an entity IS a deployment, so the
            // record must not assume one deployment per tenant.
            waitingOn: 'A client may have several deployments (D39, D41)',
            lane: L2_CLIENTS,
          },
          {
            id: 'clients.detail.pushHistory',
            path: 'push-history',
            // History (#209): the path stays so a link to it still lands.
            title: 'History',
            icon: 'send',
            perm: 'deployment.read',
            spec: 'c-fleet-console.md · CLIENTS > <client> > Push history',
            status: 'live',
            source: 'our-record',
            waitingOn: "Everything that happened to this client's deployments, pushes with their outcome among it (#209)",
            lane: L2_CLIENTS,
          },
          {
            id: 'clients.detail.egress',
            path: 'egress',
            title: 'Egress',
            icon: 'file-text',
            perm: 'egress.read',
            spec: 'c-fleet-console.md · CLIENTS > <client> > Egress',
            status: 'live',
            source: 'upward',
            waitingOn: 'What came back, per message type. Counts only (NthAI.md, boundary rule 3)',
            lane: L2_CLIENTS,
          },
          {
            id: 'clients.detail.entitlement',
            path: 'entitlement',
            title: 'Entitlement',
            icon: 'key-round',
            perm: 'entitlement.read',
            spec: 'CONSOLES.md §2.7',
            status: 'live',
            source: 'our-record',
            // Read from the same record the API enforces, so the console cannot disagree
            // with the API (2.7). Only `max_deployments` is enforced today; the screen renders
            // that from the record the checks read and says the other four are UNANSWERED
            // rather than rendering four zeroes, which would read as an entitlement decision.
            waitingOn: 'Kinds, department scopes, connector instance limits, seat counts',
            lane: L2_CLIENTS,
          },
          /*
           * THE MANIFEST WAS INCOMPLETE ON DAY ONE, and this is the amendment. Recorded here
           * rather than made quietly, because `src/nav/manifest.ts`'s own header says a route
           * added later means day one was wrong and that is the finding.
           *
           * WHAT DAY ONE MISSED. MAP is one of the six commercial lifecycle stages in
           * `src/api/clients.ts` -- DISC, PROP, WON, MAP, BUILD, RUN -- and it is the only one
           * of the six that has a WORKING SURFACE rather than just a value. The two weeks
           * between WON and BUILD produce twelve documents, nine of them contractual
           * deliverables, and an FDE runs the whole phase from a self-contained HTML page on
           * their laptop. None of that was routed.
           *
           * WHY IT WENT UNNOTICED, which is the more useful half. `routes.check.ts` asserts
           * that every subsection of CONSOLES.md 2 is claimed by a route. That direction only.
           * There is no reverse assertion, so a surface missing from BOTH the plan and the
           * manifest is invisible to the check that exists to catch exactly this -- and MAP was
           * missing from both: it appears once in the whole of CONSOLES.md, as a value in the
           * `lifecycle_stage` row of the 2.3 table, never as a screen. A one-directional guard
           * cannot see a hole that spans both of its sides. CONSOLES.md 2.3b is added in the
           * same change as these routes so the two files agree again.
           *
           * TWO ROUTES, NOT THIRTEEN. `:docId` covers all twelve documents, so filling them in
           * later changes a status and never adds a route. That is the rule holding rather than
           * being worked around: twelve sibling routes would have been thirteen chances to
           * break it.
           *
           * `source: 'our-record'` is exact and load-bearing. Every field in an engagement
           * record is ours -- our FDE's notes, our commercial terms, names the client gave us in
           * a meeting. NOTHING here came out of a customer's tenant, which is the distinction
           * that field exists to make. The client's own .xlsx is the source of truth we import
           * from and export back to; it is not a tenant read.
           */
          {
            id: 'clients.detail.map',
            path: 'map',
            title: 'MAP',
            icon: 'clipboard-list',
            perm: 'map.read',
            writes: ['map.write'],
            spec: 'CONSOLES.md §2.3b',
            status: 'live',
            source: 'our-record',
            waitingOn:
              'The twelve MAP documents over one data layer of 41 datasets, and the workbook round trip',
            lane: L2_MAP,
          },
          {
            id: 'clients.detail.mapDocument',
            path: 'map/:docId',
            title: 'MAP document',
            icon: 'file-text',
            perm: 'map.read',
            writes: ['map.write'],
            spec: 'CONSOLES.md §2.3b',
            status: 'live',
            source: 'our-record',
            // D1..D9 are contractual deliverables, W1..W3 are working artifacts. One route,
            // because they are twelve VIEWS over one shared data layer rather than twelve
            // documents with twelve stores, and the routing should say so.
            waitingOn: 'One of D1 to D9 or W1 to W3, rendered from the grid descriptors',
            lane: L2_MAP,
          },
          /*
           * THE SECOND AMENDMENT OF THE KIND §2.3b RECORDS, made in the same change as CONSOLES.md
           * §2.3c. D43 put context authoring in this console in the architecture register, and
           * nothing carried it into §2 or into this file, so the surface was missing from both and
           * the one-directional check could not see it - the MAP hole again.
           *
           * ONLY UNDER A CLIENT. There is deliberately no top-level route, no rail entry and no
           * fleet-wide list: a Company Profile is one client's material about one client, and a
           * list beside the definitions library would invite publishing it as a definition, which
           * today would deliver it to every deployment in the fleet (cc_definition_item has no
           * client column; §2.3c "Not published, yet").
           *
           * TWO ROUTES, for the reason MAP has two: `:artifactId` serves the Company Profile now and
           * a stakeholder or SOW-SCOPE later, so landing those changes nothing here.
           */
          {
            id: 'clients.detail.context',
            path: 'context',
            title: 'Context',
            icon: 'building-2',
            perm: 'context.read',
            writes: ['context.write'],
            spec: 'CONSOLES.md §2.3c',
            status: 'live',
            source: 'our-record',
            waitingOn: 'Sources read, pasted and uploaded, and the Company Profile drafted from them',
            lane: L2_CLIENTS,
          },
          {
            id: 'clients.detail.contextArtifact',
            path: 'context/:artifactId',
            title: 'Context artifact',
            icon: 'file-text',
            perm: 'context.read',
            writes: ['context.write'],
            spec: 'CONSOLES.md §2.3c',
            status: 'live',
            source: 'our-record',
            waitingOn: 'One artifact: its fields, owner, markdown, links and version history',
            lane: L2_CLIENTS,
          },
        ],
      },
    ],
  },
  {
    id: 'library',
    title: 'LIBRARY',
    routes: [
      {
        id: 'library.definitions',
        path: '/library/definitions',
        title: 'Definitions',
        icon: 'book-text',
        perm: 'definition.read',
        spec: 'CONSOLES.md §2.4',
        status: 'dayN',
        source: 'our-record',
        waitingOn: 'The sixteen kinds (D5, D31), versioned, published versus draft',
        lane: null,
        inRail: true,
      },
      {
        id: 'library.definitionKind',
        path: '/library/definitions/:kind',
        title: 'Kind',
        icon: 'book-text',
        perm: 'definition.read',
        writes: ['definition.publish'],
        spec: 'CONSOLES.md §2.4',
        status: 'live',
        source: 'our-record',
        // D35: AGENT-DEFINITION and MODEL-POLICY are REFUSED publication without a recorded
        // eval pass, and the screen shows whether the pass was the harness or a named human
        // sign-off. A gate whose provenance is invisible is a gate people learn to click
        // through. The gate is L2-console-fleet's work; the route exists on day one so that
        // landing it is a status change.
        waitingOn: 'Version history, which deployments hold which version, and the D35 publish gate',
        lane: L2,
      },
      {
        id: 'library.authoring',
        path: '/library/authoring',
        title: 'Business cases',
        icon: 'sparkles',
        perm: 'businesscase.read',
        spec: 'CONSOLES.md §2.4b',
        status: 'dayN',
        source: 'our-record',
        waitingOn: 'BUSINESS-CASE with its questions in priority order (D33, wf-00)',
        lane: null,
        inRail: true,
      },
      {
        id: 'library.businessCase',
        path: '/library/authoring/:businessCaseId',
        title: 'Business case',
        icon: 'sparkles',
        perm: 'businesscase.read',
        spec: 'CONSOLES.md §2.4b',
        status: 'dayN',
        source: 'our-record',
        // Three things this screen must do that a definitions list does not: render the
        // narrative and edit the structure SEPARATELY, showing a divergence rather than
        // merging it; show the formula ladder proposed -> feasible -> reviewed as two
        // verdicts and never one green tick; and refuse to generate from a deferred case.
        // `internal` never renders in anything a client can open.
        waitingOn:
          'Narrative beside structure, the formula ladder as two verdicts (D34), and internal never rendered to a client',
        lane: null,
      },
      {
        id: 'library.catalog',
        path: '/library/catalog',
        title: 'Catalog',
        icon: 'boxes',
        perm: 'catalog.read',
        // Was §4.5. CONSOLES.md now numbers this screen in §2 (D48), which is what the exit
        // condition reads, so the route claims its own section rather than the one that
        // described it before it had a number.
        spec: 'CONSOLES.md §2.10',
        status: 'dayN',
        source: 'our-record',
        // The master repository, and the screen a seller opens in front of a client: the
        // source systems an operator can request at onboarding, with identity.schemaClass
        // BESIDE landing.preferred, never landing alone (D32).
        //
        // §2.10 says what this EmptyState has to name, and it is not "coming soon":
        // selectability has no home. identity.catalogIds points at a store with no schema,
        // no state and no `selectable` flag, so "do not let an operator select this" lives
        // in _note strings no code reads.
        waitingOn:
          'The CATALOG kind decision in GAPS.md. Until it is answered there is nowhere to record ' +
          'that a source is not selectable, and a catalog that cannot refuse is a catalog that promises everything',
        lane: null,
        inRail: true,
      },
      {
        id: 'library.templates',
        path: '/library/templates',
        title: 'Templates',
        icon: 'library',
        perm: 'template.read',
        // Was §4.5, now numbered in §2 (D48). Same reason as the catalog above.
        spec: 'CONSOLES.md §2.11',
        status: 'live',
        source: 'our-record',
        // The global blueprint: CONNECTOR-TEMPLATE, everything that is the SAME across
        // customers. Promote is the act and it is a NAMED decision, never a side effect of a
        // client edit, and not before it has run clean across two clients. Bindings do not
        // transfer between customers; recognisers do.
        //
        // §2.11 names the thing that blocks the most valuable column: which clients hold
        // which version is what makes "a vendor changed their schema under 40 clients"
        // answerable, and it cannot be computed today.
        waitingOn:
          'The two version schemes in GAPS.md. DefinitionItem.version is an integer and templateVersion ' +
          'is semver with nothing relating them, so which client holds which template version cannot be answered',
        lane: L2,
        inRail: true,
      },
      {
        id: 'library.releases',
        path: '/library/releases',
        title: 'Releases',
        icon: 'package',
        // console-api has no `release.read`: the release list and its notices are read under
        // `deployment.read`, and offering one to a deployment (Offer update) sets its target release, which is
        // `deployment.write` (nth-ai/fleet-api#187). The manifest names the capabilities the server
        // actually checks rather than one nobody can be granted.
        perm: 'deployment.read',
        writes: ['deployment.write'],
        spec: 'CONSOLES.md §2.6',
        status: 'live',
        source: 'our-record',
        // The worked example of why the manifest is built complete: it went live as a change of
        // status (2.6), on 14 October with nth-ai/fleet-api#187. What it shows is the
        // managed-application releases, their notes and image digests, and which deployments
        // were offered each; it offers one to chosen ones. Channels, cohorts, staged rollout, a
        // minimum schema version and a rollback target are still phase 4 and not on it.
        waitingOn:
          'Channels, cohorts and staged rollout (phase 4), a minimum schema version and a rollback target (wf-03)',
        lane: L2,
        inRail: true,
      },
    ],
  },
  {
    id: 'platform',
    title: 'PLATFORM',
    routes: [
      {
        id: 'platform.entitlement',
        path: '/platform/entitlement',
        title: 'Entitlement',
        icon: 'key-round',
        perm: 'entitlement.read',
        spec: 'CONSOLES.md §2.7',
        status: 'dayN',
        source: 'our-record',
        waitingOn: 'Entitlement across clients, read from the same record the API enforces',
        lane: null,
        crossClient: true,
        inRail: true,
      },
      {
        id: 'platform.ledger',
        path: '/platform/ledger',
        title: 'Ledger',
        icon: 'receipt',
        perm: 'ledger.read',
        spec: 'CONSOLES.md §2.9',
        status: 'dayN',
        source: 'our-record',
        // Writes only. No invoice generation in this window (2.9, README 2).
        waitingOn: 'Certified events, hash chained, filterable by client and period. Writes only',
        lane: null,
        crossClient: true,
        inRail: true,
      },
      {
        id: 'platform.users',
        path: '/platform/users',
        title: 'User management',
        icon: 'users-round',
        perm: 'user.read',
        writes: ['user.write', 'role.write'],
        spec: 'c-console-access.md · Navigation',
        status: 'live',
        source: 'our-record',
        waitingOn: 'Database-managed users, custom roles and global/client capability scopes',
        lane: CONSOLE_ACCESS,
        crossClient: true,
        inRail: true,
      },
      {
        id: 'platform.audit',
        path: '/platform/audit',
        title: 'Audit',
        icon: 'activity',
        perm: 'audit.read',
        spec: 'c-fleet-console.md · PLATFORM > Audit',
        status: 'live',
        source: 'our-record',
        waitingOn: 'Who did what on this console, with the actor named',
        lane: CONSOLE_ACCESS,
        crossClient: true,
        inRail: true,
      },
    ],
  },
  {
    id: 'decks',
    title: 'DECKS',
    routes: [
      {
        id: 'decks.list',
        path: '/decks',
        title: 'Decks',
        icon: 'presentation',
        perm: 'deck.read',
        // Importing a release of the deck repo, and passing a build that failed prep (D56).
        writes: ['deck.write', 'deck.override'],
        spec: 'CONSOLES.md §2.12',
        status: 'live',
        source: 'our-record',
        waitingOn: 'Every published deck, with its active grants, views and last view',
        lane: L2_DECKS,
        inRail: true,
      },
      {
        id: 'decks.upload',
        path: '/decks/upload',
        title: 'Add a deck',
        icon: 'upload',
        perm: 'deck.write',
        writes: ['deck.write', 'deck.override'],
        spec: 'CONSOLES.md §2.12',
        status: 'live',
        source: 'our-record',
        waitingOn: 'Publish a deck, with the prep scan findings shown before anything is stored',
        lane: L2_DECKS,
        inRail: true,
      },
      {
        // Declared AFTER `/decks/upload`: `matchRoute` takes the first match, and `:slug` would
        // otherwise claim it.
        id: 'decks.detail',
        path: '/decks/:slug',
        title: 'Deck',
        icon: 'presentation',
        perm: 'deck.read',
        // Pinning a GitHub deck to a release is deck.write; passing a build is deck.override (D56).
        writes: ['deck.write', 'deck.grant', 'deck.override'],
        spec: 'CONSOLES.md §2.12',
        status: 'live',
        source: 'our-record',
        waitingOn: 'One deck: its viewers, grants, HubSpot links and the admin preview',
        lane: L2_DECKS,
      },
      {
        id: 'decks.keys',
        path: '/deck-keys',
        title: 'Deck keys',
        icon: 'key-round',
        perm: 'deck.key.write',
        writes: ['deck.key.write'],
        spec: 'CONSOLES.md §2.12',
        status: 'live',
        source: 'our-record',
        waitingOn: 'Keys that deckctl and the deck-send skill use. A key cannot manage keys',
        lane: L2_DECKS,
        inRail: true,
      },
    ],
  },
]

/**
 * The catch-all. It is NOT one of the manifest sections: it is what proves an unknown path
 * is a named 404 rather than a blank screen, and the check asserts it is reached only by
 * paths the manifest does not claim.
 */
export const NOT_FOUND: RouteNode = {
  id: 'notFound',
  path: '*',
  title: 'Not found',
  icon: 'circle-help',
  perm: 'public',
  spec: 'CONSOLES.md §5',
  status: 'live',
  source: 'none',
  lane: 'L1-console-shell',
}

// ---------------------------------------------------------------------------
// Pure helpers. No framework, no I/O.
// ---------------------------------------------------------------------------

export interface FlatRoute extends RouteNode {
  /** The full absolute path, children joined onto their parent. */
  fullPath: string
  section: NavSectionId
  parentId: string | null
}

function joinPath(parent: string, child: string): string {
  if (child === '') return parent
  return parent.endsWith('/') ? parent + child : parent + '/' + child
}

/** Every route in the manifest, parents and children, in declaration order. */
export function flattenRoutes(manifest: NavSection[] = MANIFEST): FlatRoute[] {
  const out: FlatRoute[] = []
  const walk = (node: RouteNode, section: NavSectionId, base: string, parentId: string | null) => {
    const fullPath = parentId === null ? node.path : joinPath(base, node.path)
    out.push({ ...node, fullPath, section, parentId })
    for (const child of node.children ?? []) walk(child, section, fullPath, node.id)
  }
  for (const section of manifest) {
    for (const route of section.routes) walk(route, section.id, '', null)
  }
  return out
}

/** The routes a URL can actually land on: a leaf, or a parent that declares no children. */
export function leafRoutes(manifest: NavSection[] = MANIFEST): FlatRoute[] {
  return flattenRoutes(manifest).filter((r) => !r.children || r.children.length === 0)
}

export function routeById(id: string, manifest: NavSection[] = MANIFEST): FlatRoute | null {
  return flattenRoutes(manifest).find((r) => r.id === id) ?? null
}

/**
 * Resolve a concrete path against the manifest. Returns the matched route and its params,
 * or `null`, and `null` is what NOT_FOUND renders. Deliberately tiny and dependency-free:
 * the router does the real matching at runtime, and this exists so the lane's exit condition
 * ("every route resolves, none 404s") can be asserted with nothing installed.
 */
export function matchRoute(
  path: string,
  manifest: NavSection[] = MANIFEST,
): { route: FlatRoute; params: Record<string, string> } | null {
  const wanted = segments(path)
  for (const route of leafRoutes(manifest)) {
    const params = matchSegments(segments(route.fullPath), wanted)
    if (params) return { route, params }
  }
  return null
}

function segments(path: string): string[] {
  return path.split('/').filter((s) => s.length > 0)
}

function matchSegments(pattern: string[], actual: string[]): Record<string, string> | null {
  if (pattern.length !== actual.length) return null
  const params: Record<string, string> = {}
  for (let i = 0; i < pattern.length; i++) {
    const p = pattern[i]
    if (p.startsWith(':')) {
      params[p.slice(1)] = actual[i]
    } else if (p !== actual[i]) {
      return null
    }
  }
  return params
}

/** The rail, as declared: sections carrying only the items that belong on it. */
export function railSections(manifest: NavSection[] = MANIFEST): NavSection[] {
  return manifest.map((s) => ({ ...s, routes: s.routes.filter((r) => r.inRail) }))
}

// ---------------------------------------------------------------------------
// What the Sidebar renders. Same shape and the same reasoning as the Admin
// Console's resolveNavGroups.
// ---------------------------------------------------------------------------

export interface ResolvedNavItem {
  id: string
  href: string
  name: string
  icon: string
  count?: number
}

export interface ResolvedNavGroup {
  label: string
  items: ResolvedNavItem[]
}

export interface ResolveNavOptions {
  /**
   * Capabilities the signed-in operator holds, from the entitlement record. Undefined means
   * "not loaded yet" and shows everything, so the rail does not flicker items away on first
   * paint. An empty array means they genuinely hold nothing.
   *
   * Hiding is NOT enforcement. The server refuses regardless, and it answers 404 rather than
   * 403. This only stops the rail offering doors that open onto a refusal.
   */
  capabilities?: string[]
  /** Live counts for the rail pills. Undefined stays undefined: no pill beats a lying zero. */
  counts?: Record<string, number>
}

/**
 * Turns the manifest into what the Sidebar renders. Always returns at least one group: no
 * group carries a constraint that can empty it, so the rail can never come back blank.
 */
export function resolveNavGroups({ capabilities, counts = {} }: ResolveNavOptions = {}): ResolvedNavGroup[] {
  const holds = (perm: string) => capabilities === undefined || capabilities.includes(perm)

  return railSections()
    .map((section) => ({
      label: section.title,
      items: section.routes
        .filter((item) => holds(item.perm))
        .map((item) => ({
          id: item.id,
          href: item.path,
          name: item.title,
          icon: item.icon,
          count: counts[item.id],
        })),
    }))
    .filter((group) => group.items.length > 0)
}

/**
 * The breadcrumb for a URL, taken from the manifest so the rail and the crumb cannot
 * disagree. A path the manifest does not own says so rather than guessing.
 */
export function crumbsFor(pathname: string): string[] {
  const hit = matchRoute(pathname)
  if (!hit) return ['Fleet Console', 'Not found']

  const trail: string[] = []
  let current: FlatRoute | null = hit.route
  while (current) {
    trail.unshift(current.title)
    current = current.parentId ? routeById(current.parentId) : null
  }

  const section = MANIFEST.find((s) => s.id === hit.route.section)
  const sectionTitle = section ? section.title.charAt(0) + section.title.slice(1).toLowerCase() : 'Fleet'

  // Collapse a repeated neighbour, and do it AFTER the section title is on the front rather
  // than before. Two different routes hit this and the dedupe used to catch only one of them:
  // an index child repeating its parent ("Deployment / Deployment"), and a top-level route
  // whose own title is its section's ("Clients / Clients", which is what /clients rendered).
  // Deduping the trail alone could never see the second, because the collision is between the
  // section title and the first trail entry and the section title was not in the list yet.
  const full = [sectionTitle, ...trail]
  return full.filter((title, i) => i === 0 || title !== full[i - 1])
}

// ---------------------------------------------------------------------------
// Copy for routes that are declared but not built yet. DERIVED from the route,
// never hand-written per route: thirty bespoke empty states would drift the
// moment one of them landed. Same reasoning as the Admin Console's unbuiltCopy.
// ---------------------------------------------------------------------------

/** The eyebrow above an unbuilt route: TITLE, then who lands it. */
export function unbuiltEyebrow(route: RouteNode): string {
  return `${route.title.toUpperCase()} · ${route.lane ? route.lane.toUpperCase() : 'NO LANE YET'}`
}

/** The one sentence an unbuilt route shows. Ends in a period. */
export function unbuiltCopy(route: RouteNode): string {
  if (route.status === 'live') return `${route.title} is built.`
  return route.waitingOn ? `${route.waitingOn}.` : `${route.title} is declared and not built.`
}
